import { z } from "zod";

/**
 * Invoices (Phase 5). Deposit + balance invoices are auto-drafted by the API
 * on quote-accept (deposit) and on event creation (balance, due = event_date
 * - 7d); `addon` is the only kind the UI creates directly, via POST
 * /v2/invoices. See useInvoices.ts.
 */

export const InvoiceKindSchema = z.enum(["deposit", "balance", "addon"]);
export type InvoiceKind = z.infer<typeof InvoiceKindSchema>;

export const INVOICE_KIND_LABELS: Record<InvoiceKind, string> = {
  deposit: "Deposit",
  balance: "Balance",
  addon: "Add-on",
};

export const INVOICE_KIND_BADGE_VARIANT: Record<
  InvoiceKind,
  "default" | "success" | "warning" | "danger" | "info" | "secondary"
> = {
  deposit: "info",
  balance: "secondary",
  addon: "default",
};

// Contract's status union includes "partial" alongside the Phase-5-section
// list in the plan doc — the authoritative API contract wins.
export const InvoiceStatusSchema = z.enum([
  "draft",
  "sent",
  "paid",
  "overdue",
  "void",
  "partial",
]);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: "Draft",
  sent: "Sent",
  paid: "Paid",
  overdue: "Overdue",
  void: "Void",
  partial: "Partial",
};

export const INVOICE_STATUS_BADGE_VARIANT: Record<
  InvoiceStatus,
  "default" | "success" | "warning" | "danger" | "info" | "secondary"
> = {
  draft: "secondary",
  sent: "info",
  paid: "success",
  overdue: "danger",
  void: "default",
  partial: "warning",
};

export const InvoiceLineItemSchema = z.object({
  label: z.string(),
  qty: z.number(),
  unit_cents: z.number(),
  amount_cents: z.number(),
});
export type InvoiceLineItem = z.infer<typeof InvoiceLineItemSchema>;

export const InvoiceSchema = z.object({
  id: z.string(),
  event_id: z.string(),
  kind: InvoiceKindSchema,
  number: z.string(),
  line_items: z.array(InvoiceLineItemSchema),
  subtotal_cents: z.number(),
  total_cents: z.number(),
  status: InvoiceStatusSchema,
  due_date: z.string().nullable(),
  pdf_key: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
  // Contract marks this optional (not every backend revision resolves it
  // onto the row); callers must default to 0 — see paidCents() below.
  paid_cents: z.number().optional(),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

export function paidCents(invoice: Invoice): number {
  return invoice.paid_cents ?? 0;
}

export const InvoiceListResponseSchema = z.object({
  items: z.array(InvoiceSchema),
  next_cursor: z.string().nullable(),
});
export type InvoiceListResponse = z.infer<typeof InvoiceListResponseSchema>;

export interface GenerateInvoicePdfResult {
  pdf_key?: string | null;
  url?: string;
}

export const GenerateInvoicePdfResultSchema = z.object({
  pdf_key: z.string().nullable().optional(),
  url: z.string().optional(),
});

// ---- Mutation inputs -----------------------------------------------------

export interface InvoiceLineItemInput {
  label: string;
  qty: number;
  unit_cents: number;
  amount_cents: number;
}

export interface CreateInvoiceInput {
  event_id: string;
  kind: "addon";
  line_items: InvoiceLineItemInput[];
  due_date?: string | null;
}

export interface PatchInvoiceInput {
  id: string;
  line_items?: InvoiceLineItemInput[];
  due_date?: string | null;
  notes?: string | null;
}
